import { Form, router } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import ShopifyPreviewCard from "@/components/brand/settings/integrations/shopify-preview-card"
import SyncProgressTicker from "@/components/brand/settings/products/sync-progress-ticker"
import InputError from "@/components/input-error"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { toMessages } from "@/lib/form-errors"
import { formatDateTime } from "@/lib/format-datetime"
import { brandShopifyStoreCheckPath, brandShopifyStorePath } from "@/routes"
import type {
  BrandIntegrationsShopifyProps,
  ShopifyPreviewItem,
  ShopifyPreviewState,
  ShopifyProbeReason,
} from "@/types/integrations"

/**
 * OKS-930 — Brand Settings → Integrations → Shopify store card.
 *
 * Second card on the page, below `<TiktokIntegrationCard />`. The two are
 * fully independent — neither one's status, error or flash affects the
 * other. They share only `canManage`.
 *
 * ⚠️ OKS-960 REPLACES the state list that stood here. The not-connected side
 * is no longer one form with a probing flag — it is a THREE-STEP WIZARD
 * (address → checking → "is this your store?"), whose step is derived from
 * server props rather than stored. See `SetupWizard`.
 *
 * What did not change: the connected panel is still a status surface, and the
 * non-admin read-only view still has its controls DOM-ABSENT rather than
 * disabled.
 *
 * Every form here is same-origin, so all three use Inertia `<Form>`. The
 * native `TiktokOAuthStartForm` is deliberately NOT reused — it exists
 * only because cross-origin OAuth cannot ride an Inertia XHR.
 */

/** Curated list (PRD D3). `items` is required — Base UI `SelectValue`
 *  renders the RAW value otherwise, so the trigger would read "EUR"
 *  instead of "EUR (€)". */
const CURRENCY_ITEMS: Record<string, string> = {
  EUR: "EUR (€)",
  USD: "USD ($)",
  GBP: "GBP (£)",
  CHF: "CHF (Fr.)",
  CAD: "CAD ($)",
}

/** Reasons the brand fixes by editing the address. These move focus back
 *  to the field and mark it `aria-invalid` (UX §6). `rate_limited` and
 *  `store_unavailable` are excluded on purpose: both explicitly tell the
 *  brand the address is fine, so pointing at the field would contradict
 *  the copy. */
const ADDRESS_FIXABLE: ReadonlySet<string> = new Set([
  "not_shopify",
  "not_found",
  "unreachable",
])

/**
 * OKS-962 — The copy for ANY `currency` error, at both sites that can render
 * one: the step-3 select and the connected panel's currency edit.
 *
 * ⚠️ A TOTAL map, deliberately unlike `shop_domain`'s `=== "blank" ? copy : raw`
 * conditional. The field's server-side alphabet is closed by construction —
 * `presence` + `inclusion` over a curated five — so there is no other message
 * worth preserving, and a conditional would leave a fallthrough that renders
 * whatever arrives. The server sends `BrandShopifyStore::CURRENCY_TOKEN`; if
 * anything else ever reaches here it is still a currency error and this is still
 * the right sentence.
 *
 * ⚠️ Because it is total, the Vitest example over this is nearly
 * self-satisfying — it passes for whatever the server sends. The load-bearing
 * assertion that the bag carries the TOKEN is server-side, in the request specs
 * (PRD §4). Do not read a green here as proof the server was fixed.
 *
 * Shared rather than inlined twice: a token mapped at one render site and raw at
 * the other is strictly worse than the fragment it replaced, and two copies is
 * how that happens.
 */
function currencyMessages(
  raw: unknown,
  t: (key: string) => string,
): string[] | undefined {
  const present = toMessages(raw as string | string[] | undefined)
  if (!present) return undefined

  return [t("settings.brand.integrations.shopify.currency_invalid")]
}

export function ShopifyIntegrationCard({
  store,
  submitted_domain,
  submitted_currency,
  preview,
  preview_state,
  preview_domain,
  canManage,
  live = false,
}: BrandIntegrationsShopifyProps & {
  canManage: boolean
  /** OKS-960 — signals are arriving for THIS viewer, so S2's status line may
   *  claim liveness. Defaults false: a card rendered without the live hook
   *  must not spin. */
  live?: boolean
}) {
  const { t } = useTranslation()

  const isConnected = store?.status === "connected"
  const isErrored = store?.status === "error"

  const badge = (() => {
    if (isConnected) {
      return {
        variant: "secondary" as const,
        label: t("settings.brand.integrations.shopify.status.connected"),
      }
    }
    if (isErrored) {
      return {
        variant: "destructive" as const,
        label: t("settings.brand.integrations.shopify.status.error"),
      }
    }
    return {
      variant: "outline" as const,
      label: t("settings.brand.integrations.shopify.status.not_connected"),
    }
  })()

  return (
    <Card data-testid="brand-shopify-integration-card">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2">
          {t("settings.brand.integrations.shopify.label")}
        </CardTitle>
        <Badge variant={badge.variant} data-testid="brand-shopify-status-badge">
          {badge.label}
        </Badge>
      </CardHeader>

      <CardContent className="space-y-4">
        {store ? (
          <ConnectedBody store={store} canManage={canManage} live={live} />
        ) : (
          <SetupWizard
            submittedDomain={submitted_domain}
            submittedCurrency={submitted_currency}
            preview={preview}
            previewState={preview_state}
            previewDomain={preview_domain}
            canManage={canManage}
          />
        )}
      </CardContent>
    </Card>
  )
}

/**
 * The three-step setup wizard: address → checking → "is this your store?".
 *
 * ## The step is DERIVED, not stored
 *
 *   no preview data       → 1
 *   check in flight       → 2
 *   preview data present  → 3
 *
 * plus one piece of local state (`restarted`) for **Use a different address**,
 * which returns to step 1 without a round trip — the Spec asks for a content
 * swap, not a navigation.
 *
 * ⚠️ No `localStorage` / `sessionStorage`. The two ratified per-device
 * carve-outs are the appearance theme and the sidebar collapsed state, and
 * wizard position is neither. Deriving from server props is also what makes a
 * failed check return to step 1 for free: the preview prop is simply absent.
 *
 * ## Step 3 has THREE presentations, and conflating any two is a lie
 *
 * `ready` shows the grid. `empty` says the store publishes nothing — a real,
 * actionable signal. `unavailable` says we could not read a sample.
 *
 * ⚠️ `unavailable` must never render as `empty` (AC18): that panel asserts the
 * brand's store publishes no products, and here we do not know what it
 * publishes. It must not silently look like `ready` either — this step exists
 * so the brand confirms the store is THEIRS, and in that state the confirmation
 * did not happen, so the copy says the address works, that we could not read
 * the contents, and to check the domain shown before proceeding.
 *
 * **Connect stays enabled in all three.** The reachability verdict is what
 * gates connecting and it has already passed; blocking would turn a transient
 * 429 into an unconnectable store.
 */
function SetupWizard({
  submittedDomain,
  submittedCurrency,
  preview,
  previewState,
  previewDomain,
  canManage,
}: {
  submittedDomain: string | null
  submittedCurrency: string | null
  preview: ShopifyPreviewItem[]
  previewState: ShopifyPreviewState
  previewDomain: string | null
  canManage: boolean
}) {
  const { t, i18n } = useTranslation()
  const domainRef = useRef<HTMLInputElement>(null)
  const stepThreeHeadingRef = useRef<HTMLHeadingElement>(null)

  // Local only: "go back and edit the address". The stale preview row is
  // harmless — the next check replaces it, connect deletes it, and it expires.
  const [restarted, setRestarted] = useState(false)
  const [currency, setCurrency] = useState(submittedCurrency ?? "EUR")

  const hasPreview = previewState !== "none" && !restarted

  // ⚠️ Focus follows the brand back to step 1.
  //
  // "Use a different address" unmounts the button that was just clicked, so
  // without this focus falls to the document body: a keyboard or screen-reader
  // user takes a deliberate action and is dumped at the top of the page with no
  // indication anything happened. The action is theirs, so the response has to
  // be perceivable to them.
  //
  // Same reasoning as the two focus moves already in this file — the
  // address-fixable error (→ the field) and entering step 3 (→ the heading):
  // put the user on the thing they now need. Here that is unambiguously the
  // address input, since correcting it is the entire reason they came back.
  //
  // Keyed on the transition INTO the restarted state, not on `hasPreview`: the
  // same render also happens when a check fails, and that path already moves
  // focus via `FocusOnError` — stealing it here would fight that and land the
  // user in the same place by two mechanisms, one of which would win at random.
  useEffect(() => {
    if (restarted) domainRef.current?.focus()
  }, [restarted])

  // ⚠️ Body copy renders on every step, and on step 3 it is the promise that
  // nothing has been saved yet — which is the whole reason the brand is
  // willing to look at this screen.
  const bodyCopy = (
    <p className="text-muted-foreground text-sm">
      {t("settings.brand.integrations.shopify.body_disconnected")}
    </p>
  )

  if (!canManage) {
    // Non-manage members: the whole form and both action rows are DOM-ABSENT,
    // never disabled. The page-level readonly hint already explains why.
    return bodyCopy
  }

  return hasPreview ? (
    <StepThree
      preview={preview}
      previewState={previewState}
      previewDomain={previewDomain}
      currency={currency}
      onCurrencyChange={setCurrency}
      onRestart={() => setRestarted(true)}
      headingRef={stepThreeHeadingRef}
      language={i18n.language}
      bodyCopy={bodyCopy}
    />
  ) : (
    <StepOneAndTwo
      submittedDomain={submittedDomain}
      domainRef={domainRef}
      bodyCopy={bodyCopy}
      onCheckSucceeded={() => setRestarted(false)}
    />
  )
}

/** Steps 1 and 2 are ONE form: the panel keeps its shape across a failed
 *  check, so the brand corrects rather than restarts (shipped OKS-930
 *  behaviour, preserved). Step 2 is that same form mid-flight. */
function StepOneAndTwo({
  submittedDomain,
  domainRef,
  bodyCopy,
  onCheckSucceeded,
}: {
  submittedDomain: string | null
  domainRef: React.RefObject<HTMLInputElement | null>
  bodyCopy: React.ReactNode
  onCheckSucceeded: () => void
}) {
  const { t } = useTranslation()

  return (
    <Form
      action={brandShopifyStoreCheckPath()}
      method="post"
      options={{ preserveScroll: true }}
      /* ⚠️ OKS-962 — cleared by the Form's TOP-LEVEL `onSuccess` prop. Three
         earlier wirings failed, two of them silently, and the reasons are the
         whole point of this comment:

         1. `options: { onSuccess }` — not a member of `FormComponentOptions`,
            so TypeScript rejects it. Caught by `npm run check`; invisible to a
            bare `tsc -p tsconfig.json`, which reads zero files.
         2. `onSubmitComplete` — real and typed, but the Inertia mock in
            `test/setup.tsx` DECLARES it and never invokes it, so the behaviour
            would have been unverifiable at the unit layer.
         3. `onSubmit` — ⚠️ **shipped, green under Vitest, and DEAD in a real
            browser.** The Form builds its element as
            `createElement("form", {...props, …, onSubmit: …})`: the consumer's
            `onSubmit` arrives inside `props` and the Form's own key, written
            LATER in the same object literal, overwrites it. The handler never
            runs in production. The unit layer could not see this because the
            mock spreads `...rest` onto its `<form>` and sets no `onSubmit` of
            its own, so `fireEvent.submit` called the very handler the real
            component discards. PO found it by walking the app; the flag stayed
            set, a successful re-check landed a preview the card refused to
            render, and the brand sat on step 1 with no message.

         `onSuccess` is destructured by the Form and passed into the visit, and
         `@inertiajs/core` awaits `setPage()` before calling it — so the new
         preview props are already in place when the flag clears. It is also the
         semantically correct moment, which clearing on submit was not: at
         submit time the PREVIOUS preview row is still in props, so dropping the
         flag then would swap the brand to a stale step 3 mid-flight and eat the
         step-2 skeleton. A failed check gets `onError` instead, leaves the flag
         set, and returns to step 1 on its own — the service deletes the preview
         row, so the prop is absent either way. */
      onSuccess={onCheckSucceeded}
    >
      {({ processing, errors }) => {
        // The server sends a TOKEN on `probe` and human messages on the field
        // keys — never both.
        //
        // ⚠️ `toMessages` is not decoration. `inertia_rails` FLATTENS a
        // single-message field error to a bare string, so the wire reality is
        // `string | string[]` and the declared type lies. Reading
        // `errors.probe[0]` would yield "u" — the first character of
        // "unreachable" — and every reason would fall through to no copy.
        const reason = toMessages(
          errors.probe as unknown as string | string[] | undefined,
        )?.[0] as ShopifyProbeReason | undefined
        const addressFixable = reason ? ADDRESS_FIXABLE.has(reason) : false

        const rawDomainErrors = toMessages(
          errors.shop_domain as unknown as string | string[] | undefined,
        )
        const domainErrors =
          rawDomainErrors?.[0] === "blank"
            ? [t("settings.brand.integrations.shopify.domain_blank")]
            : rawDomainErrors

        return (
          <div
            className="space-y-4"
            data-testid="brand-shopify-setup-wizard"
            data-step={processing ? "2" : "1"}
          >
            <FocusOnError active={addressFixable} target={domainRef} />

            <p className="text-muted-foreground text-sm">
              {t("settings.brand.integrations.shopify.setup.step_of", {
                current: processing ? 2 : 1,
                total: 3,
              })}
            </p>

            {bodyCopy}

            {reason ? (
              <Alert
                variant="destructive"
                role="alert"
                data-testid="brand-shopify-error-alert"
              >
                <AlertDescription>
                  {t(`settings.brand.integrations.shopify.errors.${reason}`)}
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="grid gap-2" data-testid="brand-shopify-setup-step">
              <Label htmlFor="shopify-shop-domain">
                {t("settings.brand.integrations.shopify.domain_label")}
              </Label>
              <p
                id="shopify-shop-domain-helper"
                className="text-muted-foreground text-sm"
              >
                {t("settings.brand.integrations.shopify.domain_helper")}
              </p>
              {/* type="text", NOT type="url" — native constraint validation on
                  a real <form> would reject a bare `paalm.co` before it reached
                  the server, making D1's forgiveness unreachable and routing
                  the failure through a browser string we do not control.
                  inputMode gives the keyboard without the validation. */}
              <Input
                ref={domainRef}
                id="shopify-shop-domain"
                name="shopify_store[shop_domain]"
                type="text"
                inputMode="url"
                autoCapitalize="none"
                autoComplete="off"
                spellCheck={false}
                disabled={processing}
                defaultValue={submittedDomain ?? ""}
                aria-describedby={
                  domainErrors
                    ? "shopify-shop-domain-helper shopify-shop-domain-error"
                    : "shopify-shop-domain-helper"
                }
                aria-invalid={addressFixable || Boolean(domainErrors)}
                placeholder={t(
                  "settings.brand.integrations.shopify.domain_placeholder",
                )}
                data-testid="brand-shopify-domain-input"
              />
              <InputError
                id="shopify-shop-domain-error"
                role="alert"
                messages={domainErrors}
              />

              <div
                className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end"
                data-testid="brand-shopify-actions"
              >
                <Button
                  type="submit"
                  disabled={processing}
                  className="h-11 w-full sm:h-9 sm:w-auto"
                  data-testid="brand-shopify-setup-check"
                >
                  {processing
                    ? t("settings.brand.integrations.shopify.connecting")
                    : reason
                      ? t("settings.brand.integrations.shopify.retry")
                      : t("settings.brand.integrations.shopify.setup.check")}
                </Button>
              </div>

              {/* Step 2 IS the feature, not an afterthought: the brand is told
                  why it is slow (a real third-party round trip, seconds not
                  milliseconds), and six skeletons hold the step-3 grid's shape
                  so nothing shifts when it arrives. */}
              {processing ? (
                <>
                  <p
                    role="status"
                    className="text-muted-foreground text-sm"
                    data-testid="brand-shopify-probing-hint"
                  >
                    {t("settings.brand.integrations.shopify.probing_hint")}
                  </p>
                  <div
                    className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3"
                    data-testid="brand-shopify-preview-skeleton"
                  >
                    {Array.from({ length: 6 }).map((_, index) => (
                      <div key={index} className="space-y-2">
                        <Skeleton className="aspect-square w-full rounded" />
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-4 w-1/2" />
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        )
      }}
    </Form>
  )
}

/** Step 3 — "is this your store?". Commits through the UNCHANGED
 *  `brandShopifyStorePath()` POST, which re-validates and re-probes: this
 *  screen carries no trust forward, and a preview row is not an authorisation
 *  to create a store. */
function StepThree({
  preview,
  previewState,
  previewDomain,
  currency,
  onCurrencyChange,
  onRestart,
  headingRef,
  language,
  bodyCopy,
}: {
  preview: ShopifyPreviewItem[]
  previewState: ShopifyPreviewState
  previewDomain: string | null
  currency: string
  onCurrencyChange: (value: string) => void
  onRestart: () => void
  headingRef: React.RefObject<HTMLHeadingElement | null>
  language: string
  bodyCopy: React.ReactNode
}) {
  const { t } = useTranslation()

  // The step change is announced by MOVING FOCUS, not by a live region — a
  // live region here would compete with the probing hint that just fired.
  useEffect(() => {
    headingRef.current?.focus()
  }, [headingRef])

  return (
    <Form
      action={brandShopifyStorePath()}
      method="post"
      options={{ preserveScroll: true }}
    >
      {({ processing, errors }) => (
        <div
          className="space-y-4"
          data-testid="brand-shopify-setup-wizard"
          data-step="3"
        >
          <p className="text-muted-foreground text-sm">
            {t("settings.brand.integrations.shopify.setup.step_of", {
              current: 3,
              total: 3,
            })}
          </p>

          <div className="space-y-4" data-testid="brand-shopify-setup-step">
            {/* tabIndex={-1} so it can receive programmatic focus without
                entering the tab order. */}
            <h3
              ref={headingRef}
              tabIndex={-1}
              className="text-base font-semibold"
            >
              {t("settings.brand.integrations.shopify.setup.step3_title")}
            </h3>

            {/* break-words, NOT truncate — a clipped
                `ma-tres-longue-boutique.myshopify.com` hides exactly the
                characters the brand is here to verify. */}
            <p
              className="font-medium break-words"
              data-testid="brand-shopify-domain"
            >
              {previewDomain}
            </p>

            {bodyCopy}

            <p className="text-muted-foreground text-sm">
              {t("settings.brand.integrations.shopify.setup.step3_body")}
            </p>

            {/* The commit re-normalises server-side; this carries the host we
                actually reached rather than the raw string the brand typed. */}
            <input
              type="hidden"
              name="shopify_store[shop_domain]"
              value={previewDomain ?? ""}
            />

            {previewState === "ready" ? (
              <>
                <p className="text-muted-foreground text-sm">
                  {t(
                    "settings.brand.integrations.shopify.setup.preview_partial",
                  )}
                </p>
                {/* No count anywhere: we read only the beginning of the
                    catalogue, so any total would be a guess. */}
                <div
                  className="grid grid-cols-2 gap-3 sm:grid-cols-3"
                  data-testid="brand-shopify-preview-grid"
                >
                  {preview.map((item, index) => (
                    <ShopifyPreviewCard
                      key={`${item.title}-${index}`}
                      item={item}
                      currency={currency}
                      language={language}
                    />
                  ))}
                </div>
              </>
            ) : (
              /* ⚠️ role="status" is a REQUIRED override on both alternates: the
                 `Alert` primitive hardcodes role="alert" and spreads props
                 afterwards, so without it these become assertive regions
                 interrupting a screen reader about a non-urgent fact. Neither
                 may be variant="destructive" — nothing is broken. */
              <Alert
                variant="default"
                role="status"
                data-testid={
                  previewState === "empty"
                    ? "brand-shopify-preview-empty"
                    : "brand-shopify-preview-unavailable"
                }
              >
                <AlertTitle>
                  {previewState === "empty"
                    ? t(
                        "settings.brand.integrations.shopify.setup.preview_empty_title",
                      )
                    : t(
                        "settings.brand.integrations.shopify.setup.preview_unavailable_title",
                      )}
                </AlertTitle>
                <AlertDescription>
                  {previewState === "empty"
                    ? t(
                        "settings.brand.integrations.shopify.setup.preview_empty_body",
                      )
                    : t(
                        "settings.brand.integrations.shopify.setup.preview_unavailable_body",
                      )}
                </AlertDescription>
              </Alert>
            )}

            <Label htmlFor="shopify-currency" className="mt-2">
              {t("settings.brand.integrations.shopify.currency_label")}
            </Label>
            <p
              id="shopify-currency-helper"
              className="text-muted-foreground text-sm"
            >
              {previewState === "ready"
                ? t(
                    "settings.brand.integrations.shopify.setup.preview_price_hint",
                  )
                : t("settings.brand.integrations.shopify.currency_helper")}
            </p>
            <Select
              items={CURRENCY_ITEMS}
              name="shopify_store[currency]"
              value={currency}
              onValueChange={(value) => onCurrencyChange(String(value))}
              disabled={processing}
            >
              {/* w-full overrides the Base UI `w-fit` default — at 375px the
                  content box is ~295px and a third-width trigger clips its own
                  label. */}
              <SelectTrigger
                id="shopify-currency"
                className="w-full"
                aria-describedby="shopify-currency-helper"
                data-testid="brand-shopify-currency-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CURRENCY_ITEMS).map(([code, label]) => (
                  <SelectItem key={code} value={code}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <InputError messages={currencyMessages(errors.currency, t)} />

            {/* Primary first in DOM order; both full-width stacked at 375px. */}
            <div
              className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end"
              data-testid="brand-shopify-actions"
            >
              <Button
                type="submit"
                disabled={processing}
                className="h-11 w-full sm:h-9 sm:w-auto"
                data-testid="brand-shopify-connect"
              >
                {processing
                  ? t("settings.brand.integrations.shopify.setup.committing")
                  : t("settings.brand.integrations.shopify.connect")}
              </Button>
              {/* A real <button>, not a link: it changes local state and
                  submits nothing. */}
              <Button
                type="button"
                variant="outline"
                onClick={onRestart}
                disabled={processing}
                className="h-11 w-full sm:h-9 sm:w-auto"
                data-testid="brand-shopify-setup-back"
              >
                {t(
                  "settings.brand.integrations.shopify.setup.different_address",
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Form>
  )
}

/** Connected + the OKS-931-only error state. */
function ConnectedBody({
  store,
  canManage,
  live,
}: {
  store: NonNullable<BrandIntegrationsShopifyProps["store"]>
  canManage: boolean
  live: boolean
}) {
  const { t, i18n } = useTranslation()
  const [currency, setCurrency] = useState(store.currency)
  const currencyChanged = currency !== store.currency
  // OKS-931 AC10 — shared with the Products page's SyncPanel, so the two
  // surfaces cannot format the same timestamp differently (PRD D7).
  const syncedAt = formatDateTime(store.last_synced_at, i18n.language)

  return (
    <>
      {/* break-words, NOT truncate — deliberately departing from the
          TikTok card. A @handle clipped is still recognisable; a clipped
          `ma-tres-longue-boutique.myshopify.com` hides exactly the
          characters the brand needs to verify (UX §3.4). */}
      <div className="min-w-0">
        {/* A bare domain with no label is ambiguous to a screen reader
            and to a scanning eye; UX §5 lists this key for that reason. */}
        <p className="text-muted-foreground text-sm">
          {t("settings.brand.integrations.shopify.connected_as")}
        </p>
        <p
          className="font-medium break-words"
          data-testid="brand-shopify-domain"
        >
          {store.shop_domain}
        </p>
      </div>

      {/* Only reachable once OKS-931 can set status: "error". Rendered
          today so that ticket does not ship a blank card. Same key
          namespace as the connect-time errors. */}
      {store.status === "error" && store.last_error_reason ? (
        <Alert
          variant="destructive"
          role="alert"
          data-testid="brand-shopify-error-alert"
        >
          <AlertDescription>
            {t(
              `settings.brand.integrations.shopify.errors.${store.last_error_reason}`,
            )}
          </AlertDescription>
        </Alert>
      ) : null}

      {/* OKS-931 — Sync summary. `last_synced_at` is now REAL (OKS-930 could
          never populate it), and the count is now real too, so the OKS-930
          prohibition on implying one no longer holds: the reason it was
          written — the probe read nothing, so any count would be a lie — is
          gone. Counts are the successor to the honesty line, not a violation
          of it.

          ⚠️ `at` is FORMATTED here, never interpolated raw. The previous
          version passed the ISO-8601 string straight into
          « Dernière synchronisation : %{at} ». It was nil-guarded and OKS-930
          could not populate the column, so the defect was unreachable — this
          ticket makes it reachable, and `formatDateTime` is shared with the
          Products page so the two surfaces cannot format it differently
          (PRD D7 / AC10). */}
      <p
        className="text-muted-foreground text-sm"
        data-testid="brand-shopify-sync-status"
      >
        {/* OKS-960 — S2's single delta: while an import runs the status line
            renders the live ticker instead of the last-import summary. Same
            component and the same keys as the Products page, so the two
            surfaces cannot describe one import differently. */}
        {store.sync_progress !== null ? (
          <SyncProgressTicker
            progress={store.sync_progress}
            pending={false}
            live={live && store.sync_progress.started_by_viewer}
            onRefresh={() => router.reload({ only: ["shopify"] })}
            refreshing={false}
          />
        ) : syncedAt !== null ? (
          <>
            {t("settings.brand.integrations.shopify.last_synced_at", {
              at: syncedAt,
            })}
            {store.last_sync_product_count !== null ? (
              <>
                {" · "}
                <span data-testid="brand-shopify-product-count">
                  {t("settings.brand.products.sync.product_count", {
                    count: store.last_sync_product_count,
                  })}
                </span>
              </>
            ) : null}
          </>
        ) : (
          t("settings.brand.integrations.shopify.not_synced_yet")
        )}
      </p>

      {canManage ? (
        <>
          <Form
            action={brandShopifyStorePath()}
            method="patch"
            options={{ preserveScroll: true }}
          >
            {({ processing, errors }) => (
              <div className="grid gap-2">
                <Label htmlFor="shopify-currency-edit">
                  {t("settings.brand.integrations.shopify.currency_label")}
                </Label>
                <Select
                  items={CURRENCY_ITEMS}
                  name="shopify_store[currency]"
                  value={currency}
                  onValueChange={(value) => setCurrency(String(value))}
                  disabled={processing}
                >
                  <SelectTrigger
                    id="shopify-currency-edit"
                    className="w-full"
                    data-testid="brand-shopify-currency-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CURRENCY_ITEMS).map(([code, label]) => (
                      <SelectItem key={code} value={code}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <InputError messages={currencyMessages(errors.currency, t)} />
                {/* Appears only once the value differs from the persisted
                    one (UX §3.4) — a save button that is always present
                    on an unchanged value invites a no-op round trip. */}
                {currencyChanged ? (
                  <Button
                    type="submit"
                    variant="outline"
                    disabled={processing}
                    className="h-11 w-full sm:h-9 sm:w-auto"
                    data-testid="brand-shopify-save-currency"
                  >
                    {t("settings.brand.integrations.shopify.save_currency")}
                  </Button>
                ) : null}
              </div>
            )}
          </Form>

          <div
            className="flex flex-col gap-2 sm:flex-row sm:justify-end"
            data-testid="brand-shopify-actions"
          >
            <DisconnectAction />
          </div>
        </>
      ) : null}
    </>
  )
}

function DisconnectAction() {
  const { t } = useTranslation()

  return (
    <AlertDialog>
      <AlertDialogTrigger
        render={
          <Button
            type="button"
            variant="outline"
            className="h-11 w-full sm:h-9 sm:w-auto"
            data-testid="brand-shopify-disconnect-trigger"
          >
            {t("settings.brand.integrations.shopify.disconnect")}
          </Button>
        }
      />
      <AlertDialogContent data-testid="brand-shopify-disconnect-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("settings.brand.integrations.shopify.disconnect_confirm_title")}
          </AlertDialogTitle>
          {/* OKS-931 (PRD D12) — the copy now names the catalogue, because
              disconnect now DELETES it: `products.brand_id` cascades from
              brands, not from the store row, so `DisconnectService` removes
              the brand's products explicitly. The OKS-930 comment arguing
              against a products clause was correct when written and is
              deleted rather than annotated — a superseded rationale left in
              place is read as the contract by the next person. */}
          <AlertDialogDescription>
            {t("settings.brand.integrations.shopify.disconnect_confirm_body")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Form
          action={brandShopifyStorePath()}
          method="delete"
          options={{ preserveScroll: true }}
        >
          {({ processing }) => (
            <AlertDialogFooter>
              <AlertDialogCancel
                render={
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="brand-shopify-disconnect-cancel"
                  >
                    {t(
                      "settings.brand.integrations.shopify.disconnect_confirm_cancel",
                    )}
                  </Button>
                }
              />
              {/* Plain submit — NOT AlertDialogAction, which is a Close
                  and would tear the form down before Inertia submits.
                  Same detail as the TikTok card. */}
              <Button
                type="submit"
                variant="destructive"
                disabled={processing}
                data-testid="brand-shopify-disconnect-confirm"
              >
                {processing
                  ? t("settings.brand.integrations.shopify.disconnecting")
                  : t(
                      "settings.brand.integrations.shopify.disconnect_confirm_cta",
                    )}
              </Button>
            </AlertDialogFooter>
          )}
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/** Moves focus to the address field on an address-fixable error so a
 *  keyboard/AT user lands on the thing to change (UX §6). Rendered as a
 *  component so the hook is not called conditionally. */
function FocusOnError({
  active,
  target,
}: {
  active: boolean
  target: React.RefObject<HTMLInputElement | null>
}) {
  useEffect(() => {
    if (active) target.current?.focus()
  }, [active, target])

  return null
}

export default ShopifyIntegrationCard
